import { authenticatedInstance } from '../index/index.service';
import { defaultStreamUrl, subscribeStream } from '../sse-subscribe/sse-subscribe.util';
import type { EntityId, PrintJobStatus, PrintJobView, PrinterView, PrinterAdminView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { PrintJobStatus, PrintJobView, PrinterView, PrinterAdminView } from 'shared-models/browser';

export type PrinterConfigPatch = { billable?: boolean; humanDisabled?: boolean };

export type PrinterTestPrintResult = {
  ok: true;
  job: EntityId & {
    status: PrintJobStatus;
    printerId: number;
    printerName: string;
    printableText: string;
    createdAt: string;
  };
};

export type PrintJobStreamEvent = {
  type: 'upsert';
  job: PrintJobView;
};

class PrintersService {
  // Summary: Loads active tenant printers for manager product-printer assignment UI under POS settings.
  // Details: docs/function-library/web/services.md#printers-service
  async listActive(
    tenantId: string,
    employeeSession: string,
    options?: { bypassCache?: boolean },
  ): Promise<PrinterView[]> {
    try {
      const response = await authenticatedInstance.get<PrinterView[]>(`/printers/tenant/${tenantId}/active`, {
        headers: {
          'x-employee-session': employeeSession,
        },
        // The endpoint sets Cache-Control max-age=10; fire-time routing must not read a stale list.
        ...(options?.bypassCache ? { params: { _t: Date.now() } } : {}),
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Super-admin list of ALL tenant printers (incl. inactive/disabled). Super-admin JWT.
  async listManagedPrinters(tenantId: string): Promise<PrinterAdminView[]> {
    try {
      const response = await authenticatedInstance.get<PrinterAdminView[]>(
        `/printers/tenant/${encodeURIComponent(tenantId)}/manage`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Diff-only super-admin toggle of a printer's human flags (billable/humanDisabled); 204.
  async updatePrinterConfig(tenantId: string, printerId: number, patch: PrinterConfigPatch): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/printers/tenant/${encodeURIComponent(tenantId)}/${printerId}/config`,
        patch,
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Manager set/clear of a printer's redirect target; null clears; 204.
  async updatePrinterRoute(
    tenantId: string,
    printerId: number,
    routeToPrinterId: number | null,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/printers/tenant/${encodeURIComponent(tenantId)}/${printerId}/route`,
        { routeToPrinterId },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Requests a server-generated test print payload for a selected active tenant printer so manager settings can validate print rout...
  // Details: docs/function-library/web/services.md#printers-service
  async createTestPrint(
    tenantId: string,
    printerId: number,
    employeeSession: string,
  ): Promise<PrinterTestPrintResult> {
    try {
      const response = await authenticatedInstance.post<PrinterTestPrintResult>(
        `/printers/tenant/${tenantId}/${printerId}/test-print`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Manager "פתח מגירה" — enqueues a cash-drawer kick job for the tenant's cash-drawer printer.
  async openCashDrawer(
    tenantId: string,
    employeeSession: string,
    printerId?: number,
  ): Promise<{ ok: true; printerId: number; printerName: string }> {
    try {
      const response = await authenticatedInstance.post<{ ok: true; printerId: number; printerName: string }>(
        `/printers/tenant/${tenantId}/open-cash-drawer`,
        printerId !== undefined ? { printerId } : {},
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Loads recent tenant print jobs for settings-level diagnostics and agent lifecycle monitoring UI.
  // Details: docs/function-library/web/services.md#printers-service
  async listPrintJobs(tenantId: string, employeeSession: string, limit = 25): Promise<PrintJobView[]> {
    try {
      const response = await authenticatedInstance.get<PrintJobView[]>(
        `/printers/tenant/${tenantId}/print-jobs?limit=${limit}`,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Reports print job lifecycle transitions from trusted clients to backend authoritative status history.
  // Details: docs/function-library/web/services.md#printers-service
  async updatePrintJobStatus(
    tenantId: string,
    jobId: number,
    status: PrintJobStatus,
    employeeSession: string,
    errorMessage?: string,
  ): Promise<PrintJobView> {
    try {
      const response = await authenticatedInstance.patch<PrintJobView>(
        `/printers/tenant/${tenantId}/print-jobs/${jobId}/status`,
        {
          status,
          errorMessage,
        },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Enqueue a visual receipt print job; returns the created job id for tracking.
  async queueOrderReceiptPrint(
    tenantId: string,
    orderId: number,
    printerId: number,
    printableImageDataUrl: string,
    employeeSession: string,
    markBillPrinted = false,
    clientClickAt?: string,
  ): Promise<number> {
    try {
      const response = await authenticatedInstance.post<{ id: number }>(
        `/printers/tenant/${encodeURIComponent(tenantId)}/order/${orderId}/print-receipt`,
        {
          printerId,
          printableImageDataUrl,
          ...(markBillPrinted ? { markBillPrinted: true } : {}),
          ...(clientClickAt ? { clientClickAt } : {}),
        },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data.id;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to queue receipt print job'));
    }
  }

  // Summary: Queues an order-less report (X/Z/worker) image print job.
  // Details: docs/function-library/web/services.md#printers-service
  async printReport(
    tenantId: string,
    printerId: number,
    printableImageDataUrl: string,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/printers/tenant/${encodeURIComponent(tenantId)}/print-report`,
        { printerId, printableImageDataUrl },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to queue report print job'));
    }
  }

  // Summary: Fetches one print job's status only (no image payload) for lightweight result polling.
  async getPrintJobStatus(
    tenantId: string,
    jobId: number,
    employeeSession: string,
  ): Promise<PrintJobStatus | null> {
    const response = await authenticatedInstance.get<{ id: number; status: PrintJobStatus }>(
      `/printers/tenant/${encodeURIComponent(tenantId)}/print-jobs/${jobId}/status`,
      { headers: { 'x-employee-session': employeeSession } },
    );
    return response.data.status;
  }

  // Summary: Poll a print job until a terminal status (printed/failed) or attempts run out.
  async pollPrintJobResult(
    tenantId: string,
    jobId: number,
    employeeSession: string,
    attempts = 8,
    intervalMs = 1200,
  ): Promise<PrintJobStatus | 'pending'> {
    for (let i = 0; i < attempts; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      try {
        const status = await this.getPrintJobStatus(tenantId, jobId, employeeSession);
        if (status === 'printed' || status === 'failed') {
          return status;
        }
      } catch {
        // transient read error; keep polling
      }
    }
    return 'pending';
  }

  // Summary: Mints a single-use SSE ticket via authenticated POST so the JWT employee-session token is never carried in URL query strings (w...
  // Details: docs/function-library/web/services.md#printers-service
  private async mintStreamTicket(tenantId: string, employeeSession: string): Promise<string> {
    const response = await authenticatedInstance.post<{ ticket: string; expiresAt: number }>(
      `/printers/tenant/${tenantId}/print-jobs/stream-ticket`,
      {},
      {
        headers: {
          'x-employee-session': employeeSession,
        },
      },
    );
    if (!response.data?.ticket) {
      throw new Error('Stream ticket missing in response');
    }
    return response.data.ticket;
  }

  // Summary: Opens tenant-scoped SSE stream for print-job updates so POS can apply server-pushed status changes with minimal polling fallback.
  // Details: docs/function-library/web/services.md#printers-service
  subscribePrintJobs(
    tenantId: string,
    employeeSession: string,
    onEvent: (event: PrintJobStreamEvent) => void,
    onError?: () => void,
  ): () => void {
    return subscribeStream<PrintJobStreamEvent>({
      mintTicket: () => this.mintStreamTicket(tenantId, employeeSession),
      buildStreamUrl: (ticket, lastEventId) =>
        defaultStreamUrl(
          `/printers/tenant/${encodeURIComponent(tenantId)}/print-jobs/stream`,
          ticket,
          lastEventId,
        ),
      parseEvent: (payload) => {
        const candidate = payload as PrintJobStreamEvent | undefined;
        if (candidate?.type === 'upsert' && candidate.job && typeof candidate.job.id === 'number') {
          return candidate;
        }
        return null;
      },
      onEvent,
      onError,
    });
  }
}

export const printersService = new PrintersService();





